import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { useLang } from "@/contexts/LanguageContext";
import { PASSPORT_HI } from "@/lib/i18n_passport";

const usePT = () => {
  const { lang, tv } = useLang();
  return (x) => (lang === "hi" ? PASSPORT_HI[x] || tv(x) : x);
};

const SECTIONS = [
  { key: "business_identity", label: "Business Identity" },
  { key: "promoter", label: "Promoter" },
  { key: "business_operations", label: "Operations" },
  { key: "financial", label: "Financial" },
  { key: "banking", label: "Banking" },
  { key: "gst", label: "GST" },
  { key: "existing_liabilities", label: "Existing Liabilities" },
  { key: "funding_requirement", label: "Funding Requirement" },
  { key: "collateral", label: "Collateral" },
  { key: "scheme_attributes", label: "Scheme Attributes" },
  { key: "declarations", label: "Declarations" },
  { key: "consent_flags", label: "Consent & Discoverability" },
];

const Input = ({ label, value, onChange, type = "text", ...rest }) => {
  const pt = usePT();
  return (
  <div className="text-sm">
    <label className="block font-medium text-slate-800 mb-1">{pt(label)}</label>
    <input type={type} value={value ?? ""} onChange={(e) => onChange(type === "number" ? Number(e.target.value) : e.target.value)} className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900" {...rest} />
  </div>
  );
};
const Toggle = ({ label, value, onChange, testId }) => {
  const pt = usePT();
  return (
  <label className="flex items-center gap-2 text-sm" data-testid={testId}>
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    <span className="text-slate-800">{pt(label)}</span>
  </label>
  );
};
const Select = ({ label, value, onChange, options = [], testId }) => {
  const pt = usePT();
  const safeOptions = Array.isArray(options) ? options : [];
  return (
  <div className="text-sm">
    <label className="block font-medium text-slate-800 mb-1">{pt(label)}</label>
    <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-slate-900 focus:ring-1 focus:ring-slate-900">
      <option value="">{pt("Select…")}</option>
      {safeOptions.map((o) => <option key={o} value={o}>{pt(o)}</option>)}
    </select>
  </div>
  );
};

export default function Passport() {
  const [biz, setBiz] = useState(null);
  const [step, setStep] = useState(0);
  const [masters, setMasters] = useState({ districts: [], industries: [], constitutions: [], msme_categories: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();
  const pt = usePT();

  useEffect(() => {
    async function boot() {
      const m = await api.get("/masters/all"); setMasters(m.data);
      const r = await api.get("/businesses");
      if (r.data.length === 0) {
        const c = await api.post("/businesses", { business_identity: {}, promoter: {}, business_operations: {}, financial: {}, banking: {}, gst: {}, existing_liabilities: [], funding_requirement: {}, collateral: [], scheme_attributes: {}, declarations: {}, consent_flags: {}, discoverability: { public_sector: true, private_sector: true, sfb: true, nbfc: false } });
        setBiz(c.data);
      } else setBiz(r.data[0]);
      setLoading(false);
    }
    boot();
  }, []);

  const setField = (section, key, value) => {
    setBiz((b) => ({ ...b, [section]: { ...(b?.[section] || {}), [key]: value } }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/businesses/${biz.id}`, biz);
      setBiz(data); toast.success(pt("Saved"));
    } catch (e) { toast.error(pt("Save failed")); }
    finally { setSaving(false); }
  };
  const next = async () => { await save(); setStep((s) => Math.min(SECTIONS.length - 1, s + 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const runAssessment = async () => {
    setSaving(true);
    try {
      await api.put(`/businesses/${biz.id}`, biz);
      const { data } = await api.post(`/businesses/${biz.id}/assess-readiness`);
      toast.success(`${pt("Readiness")}: ${pt(data.band)}`);
      nav("/borrower/readiness");
    } catch (e) { toast.error(pt("Assessment failed")); }
    finally { setSaving(false); }
  };

  if (loading || !biz) return <div className="text-slate-500">{pt("Loading…")}</div>;

  const sec = SECTIONS[step].key;
  const id = biz.business_identity || {}, pr = biz.promoter || {}, op = biz.business_operations || {},
        fn = biz.financial || {}, bk = biz.banking || {}, gs = biz.gst || {},
        fr = biz.funding_requirement || {}, sa = biz.scheme_attributes || {},
        dc = biz.declarations || {}, cf = biz.consent_flags || {};

  return (
    <div className="space-y-6" data-testid="passport-page">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 brand-heading">{pt("Financial Passport")}</h1>
          <p className="text-xs sm:text-sm text-slate-600 mt-1">{pt("Step")} {step + 1} {pt("of")} {SECTIONS.length} · {pt(SECTIONS[step].label)} · {pt("Autosaves on Next")}</p>
        </div>
        <div className="text-xs sm:text-sm text-slate-500">{pt("Completion")}: <span className="font-bold text-slate-900" data-testid="passport-completion">{biz.profile_completion || 0}%</span></div>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-slate-900 transition-all" style={{ width: `${((step + 1) / SECTIONS.length) * 100}%` }} />
      </div>

      {/* Stepper pills */}
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((s, i) => (
          <button key={s.key} onClick={() => setStep(i)} data-testid={`step-${s.key}`} className={`text-xs px-3 py-1 rounded-full border transition-colors ${i === step ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>
            {i + 1}. {pt(s.label)}
          </button>
        ))}
      </div>

      <div className="p-6 rounded-lg border border-slate-200 bg-white space-y-4">
        {sec === "business_identity" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Legal business name" value={id.legal_business_name} onChange={(v) => setField("business_identity", "legal_business_name", v)} />
            <Input label="Trade name" value={id.trade_name} onChange={(v) => setField("business_identity", "trade_name", v)} />
            <Select label="Constitution" value={id.constitution} onChange={(v) => setField("business_identity", "constitution", v)} options={masters.constitutions} testId="const-select" />
            <Input label="Udyam number" value={id.udyam_number} onChange={(v) => setField("business_identity", "udyam_number", v)} />
            <Input label="Business PAN" value={id.pan} onChange={(v) => setField("business_identity", "pan", v)} />
            <Input label="GSTIN" value={id.gstin} onChange={(v) => setField("business_identity", "gstin", v)} />
            <Select label="District" value={id.district} onChange={(v) => setField("business_identity", "district", v)} options={masters.districts} testId="district-select" />
            <Input label="PIN code" value={id.pin_code} onChange={(v) => setField("business_identity", "pin_code", v)} />
            <Select label="Industry" value={id.industry} onChange={(v) => setField("business_identity", "industry", v)} options={masters.industries} testId="industry-select" />
            <Select label="MSME category" value={id.msme_category} onChange={(v) => setField("business_identity", "msme_category", v)} options={masters.msme_categories} testId="msme-cat-select" />
            <Input label="Business vintage (years)" type="number" value={id.vintage_years} onChange={(v) => setField("business_identity", "vintage_years", v)} />
            <Input label="Employees" type="number" value={id.employees} onChange={(v) => setField("business_identity", "employees", v)} />
            <div className="md:col-span-2 flex flex-wrap gap-4 pt-2">
              <Toggle label="Women-owned enterprise" value={id.women_owned} onChange={(v) => setField("business_identity", "women_owned", v)} testId="women-owned" />
              <Toggle label="SC/ST-owned" value={id.sc_st_owned} onChange={(v) => setField("business_identity", "sc_st_owned", v)} testId="scst-owned" />
              <Toggle label="Exporter" value={id.exporter} onChange={(v) => setField("business_identity", "exporter", v)} testId="exporter" />
              <Toggle label="Startup recognised" value={id.startup} onChange={(v) => setField("business_identity", "startup", v)} testId="startup" />
            </div>
          </div>
        )}
        {sec === "promoter" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Promoter name" value={pr.name} onChange={(v) => setField("promoter", "name", v)} />
            <Input label="Role" value={pr.role} onChange={(v) => setField("promoter", "role", v)} />
            <Input label="Promoter PAN" value={pr.pan} onChange={(v) => setField("promoter", "pan", v)} />
            <Input label="Mobile" value={pr.mobile} onChange={(v) => setField("promoter", "mobile", v)} />
            <Input label="Experience (years)" type="number" value={pr.experience_years} onChange={(v) => setField("promoter", "experience_years", v)} />
            <Select label="Credit score range (indicative)" value={pr.credit_score_range} onChange={(v) => setField("promoter", "credit_score_range", v)} options={["<650", "650-700", "700-750", "750-800", "800+"]} testId="cs-range" />
          </div>
        )}
        {sec === "business_operations" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Main products / services" value={op.main_products} onChange={(v) => setField("business_operations", "main_products", v)} />
            <Input label="Monthly sales (₹)" type="number" value={op.monthly_sales} onChange={(v) => setField("business_operations", "monthly_sales", v)} />
            <Input label="Number of locations" type="number" value={op.locations} onChange={(v) => setField("business_operations", "locations", v)} />
            <Input label="Capacity utilization (%)" type="number" value={op.capacity_utilization} onChange={(v) => setField("business_operations", "capacity_utilization", v)} />
            <Input label="Top customer concentration (%)" type="number" value={op.customer_concentration} onChange={(v) => setField("business_operations", "customer_concentration", v)} />
            <Input label="Supplier concentration (%)" type="number" value={op.supplier_concentration} onChange={(v) => setField("business_operations", "supplier_concentration", v)} />
          </div>
        )}
        {sec === "financial" && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Latest revenue (₹)" type="number" value={fn.revenue_latest} onChange={(v) => setField("financial", "revenue_latest", v)} />
            <Input label="EBITDA margin (%)" type="number" value={fn.ebitda_margin} onChange={(v) => setField("financial", "ebitda_margin", v)} />
            <Input label="Net profit margin (%)" type="number" value={fn.net_profit_margin} onChange={(v) => setField("financial", "net_profit_margin", v)} />
            <Input label="Current ratio" type="number" value={fn.current_ratio} onChange={(v) => setField("financial", "current_ratio", v)} />
            <Input label="Debt/Equity" type="number" value={fn.debt_equity_ratio} onChange={(v) => setField("financial", "debt_equity_ratio", v)} />
            <Input label="Indicative DSCR" type="number" value={fn.indicative_dscr} onChange={(v) => setField("financial", "indicative_dscr", v)} />
          </div>
        )}
        {sec === "banking" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Primary bank" value={bk.primary_bank} onChange={(v) => setField("banking", "primary_bank", v)} />
            <Input label="Avg monthly credit (₹)" type="number" value={bk.avg_monthly_credit} onChange={(v) => setField("banking", "avg_monthly_credit", v)} />
            <Input label="Avg monthly balance (₹)" type="number" value={bk.avg_monthly_balance} onChange={(v) => setField("banking", "avg_monthly_balance", v)} />
            <Input label="Cheque returns in 6 months" type="number" value={bk.cheque_returns_6m} onChange={(v) => setField("banking", "cheque_returns_6m", v)} />
            <Input label="Account vintage (months)" type="number" value={bk.account_vintage_months} onChange={(v) => setField("banking", "account_vintage_months", v)} />
          </div>
        )}
        {sec === "gst" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Toggle label="GST registered" value={gs.registered} onChange={(v) => setField("gst", "registered", v)} testId="gst-registered" />
            <Toggle label="Filings regular" value={gs.filing_regular} onChange={(v) => setField("gst", "filing_regular", v)} testId="gst-regular" />
            <Input label="Last 12-month sales (₹)" type="number" value={gs.last_12m_sales} onChange={(v) => setField("gst", "last_12m_sales", v)} />
            <Input label="Last 12-month purchase (₹)" type="number" value={gs.last_12m_purchase} onChange={(v) => setField("gst", "last_12m_purchase", v)} />
          </div>
        )}
        {sec === "existing_liabilities" && (
          <LiabilityEditor items={biz.existing_liabilities || []} onChange={(v) => setBiz((b) => ({ ...b, existing_liabilities: v }))} />
        )}
        {sec === "funding_requirement" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select label="Purpose" value={fr.purpose} onChange={(v) => setField("funding_requirement", "purpose", v)} options={["Working capital", "Machinery or equipment", "Business expansion", "New business", "Loan takeover", "Other"]} testId="fr-purpose" />
            <Input label="Requested amount (₹)" type="number" value={fr.requested_amount} onChange={(v) => setField("funding_requirement", "requested_amount", v)} />
            <Input label="Minimum acceptable amount (₹)" type="number" value={fr.min_acceptable_amount} onChange={(v) => setField("funding_requirement", "min_acceptable_amount", v)} />
            <Input label="Preferred tenure (months)" type="number" value={fr.preferred_tenure_months} onChange={(v) => setField("funding_requirement", "preferred_tenure_months", v)} />
            <Input label="Moratorium (months)" type="number" value={fr.moratorium_months} onChange={(v) => setField("funding_requirement", "moratorium_months", v)} />
            <Input label="Project cost (₹)" type="number" value={fr.project_cost} onChange={(v) => setField("funding_requirement", "project_cost", v)} />
            <Input label="Promoter contribution (₹)" type="number" value={fr.promoter_contribution} onChange={(v) => setField("funding_requirement", "promoter_contribution", v)} />
            <Toggle label="Collateral available" value={fr.collateral_available} onChange={(v) => setField("funding_requirement", "collateral_available", v)} testId="fr-collateral" />
          </div>
        )}
        {sec === "collateral" && (
          <CollateralEditor items={biz.collateral || []} onChange={(v) => setBiz((b) => ({ ...b, collateral: v }))} />
        )}
        {sec === "scheme_attributes" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Toggle label="Prior scheme benefit received" value={sa.prior_scheme_benefit} onChange={(v) => setField("scheme_attributes", "prior_scheme_benefit", v)} testId="sa-prior" />
            <Toggle label="First-time entrepreneur" value={sa.first_time} onChange={(v) => setField("scheme_attributes", "first_time", v)} testId="sa-first" />
            <Input label="Domicile district" value={sa.domicile_district} onChange={(v) => setField("scheme_attributes", "domicile_district", v)} />
            <Input label="Category (Gen/OBC/SC/ST/MIN)" value={sa.category} onChange={(v) => setField("scheme_attributes", "category", v)} />
          </div>
        )}
        {sec === "declarations" && (
          <div className="space-y-3 text-sm">
            <Toggle label="All information provided is true to the best of my knowledge" value={dc.true_info} onChange={(v) => setField("declarations", "true_info", v)} testId="dec-true" />
            <Toggle label="I understand that BharatKosh is not a lender or government authority" value={dc.not_lender} onChange={(v) => setField("declarations", "not_lender", v)} testId="dec-not-lender" />
            <Toggle label="I authorize BharatKosh to share my data with matched lenders only after my explicit consent" value={dc.share_on_consent} onChange={(v) => setField("declarations", "share_on_consent", v)} testId="dec-share" />
          </div>
        )}
        {sec === "consent_flags" && (
          <div className="space-y-3 text-sm">
            <Toggle label="Discoverable to public-sector banks" value={biz.discoverability?.public_sector} onChange={(v) => setBiz((b) => ({ ...b, discoverability: { ...b.discoverability, public_sector: v } }))} testId="disc-psb" />
            <Toggle label="Discoverable to private-sector banks" value={biz.discoverability?.private_sector} onChange={(v) => setBiz((b) => ({ ...b, discoverability: { ...b.discoverability, private_sector: v } }))} testId="disc-pvb" />
            <Toggle label="Discoverable to small finance banks" value={biz.discoverability?.sfb} onChange={(v) => setBiz((b) => ({ ...b, discoverability: { ...b.discoverability, sfb: v } }))} testId="disc-sfb" />
            <Toggle label="Discoverable to NBFCs" value={biz.discoverability?.nbfc} onChange={(v) => setBiz((b) => ({ ...b, discoverability: { ...b.discoverability, nbfc: v } }))} testId="disc-nbfc" />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button onClick={back} disabled={step === 0} data-testid="passport-back" className="rounded-md border border-slate-300 px-4 py-2 text-sm disabled:opacity-40">{pt("Back")}</button>
        <div className="flex gap-2">
          <button onClick={save} disabled={saving} data-testid="passport-save" className="rounded-md border border-slate-300 px-4 py-2 text-sm">{saving ? pt("Saving…") : pt("Save")}</button>
          {step < SECTIONS.length - 1 ? (
            <button onClick={next} disabled={saving} data-testid="passport-next" className="rounded-md bg-slate-900 hover:bg-slate-800 text-white px-5 py-2 text-sm font-semibold">{pt("Save & Continue")}</button>
          ) : (
            <button onClick={runAssessment} disabled={saving} data-testid="passport-assess" className="rounded-md bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 text-sm font-semibold">{pt("Save & Run Readiness")}</button>
          )}
        </div>
      </div>

      <DisclaimerBanner compact />
    </div>
  );
}

function LiabilityEditor({ items, onChange }) {
  const pt = usePT();
  const add = () => onChange([...items, { _uid: `l-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, lender: "", facility_type: "Term Loan", sanctioned_amount: 0, outstanding: 0, emi: 0, interest_rate: 0, overdue: 0 }]);
  const upd = (i, k, v) => onChange(items.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((l, i) => (
        <div key={l._uid || `liab-fallback-${i}`} className="grid grid-cols-2 md:grid-cols-6 gap-2 p-3 rounded-md border border-slate-200 bg-slate-50">
          <input placeholder={pt("Lender")} value={l.lender} onChange={(e) => upd(i, "lender", e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" data-testid={`liab-lender-${i}`} />
          <input placeholder={pt("Facility")} value={l.facility_type} onChange={(e) => upd(i, "facility_type", e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder={pt("Sanctioned")} type="number" value={l.sanctioned_amount} onChange={(e) => upd(i, "sanctioned_amount", Number(e.target.value))} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder={pt("EMI")} type="number" value={l.emi} onChange={(e) => upd(i, "emi", Number(e.target.value))} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder={pt("Overdue")} type="number" value={l.overdue} onChange={(e) => upd(i, "overdue", Number(e.target.value))} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <button onClick={() => del(i)} className="text-sm text-red-600" data-testid={`liab-del-${i}`}>{pt("Remove")}</button>
        </div>
      ))}
      <button onClick={add} data-testid="liab-add" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">{pt("+ Add liability")}</button>
      {items.length === 0 && <div className="text-sm text-slate-500">{pt("No existing liabilities added.")}</div>}
    </div>
  );
}
function CollateralEditor({ items, onChange }) {
  const pt = usePT();
  const add = () => onChange([...items, { _uid: `c-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, type: "None", owner: "", location: "", estimated_value: 0, offered_as_collateral: false }]);
  const upd = (i, k, v) => onChange(items.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const del = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {items.map((c, i) => (
        <div key={c._uid || `col-fallback-${i}`} className="grid grid-cols-1 md:grid-cols-6 gap-2 p-3 rounded-md border border-slate-200 bg-slate-50">
          <select value={c.type} onChange={(e) => upd(i, "type", e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
            {["None","Residential property","Commercial property","Industrial property","Agricultural property","Machinery","Fixed deposit","Other security"].map((o) => <option key={o} value={o}>{pt(o)}</option>)}
          </select>
          <input placeholder={pt("Owner")} value={c.owner} onChange={(e) => upd(i, "owner", e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder={pt("Location")} value={c.location} onChange={(e) => upd(i, "location", e.target.value)} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder={pt("Estimated value")} type="number" value={c.estimated_value} onChange={(e) => upd(i, "estimated_value", Number(e.target.value))} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <label className="inline-flex items-center gap-1 text-sm"><input type="checkbox" checked={c.offered_as_collateral} onChange={(e) => upd(i, "offered_as_collateral", e.target.checked)} /> {pt("Offer as collateral")}</label>
          <button onClick={() => del(i)} className="text-sm text-red-600" data-testid={`col-del-${i}`}>{pt("Remove")}</button>
        </div>
      ))}
      <button onClick={add} data-testid="col-add" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">{pt("+ Add collateral")}</button>
    </div>
  );
}
